import { Col, Divider, List, Modal, Row, Spin, Table, Tooltip } from "antd";
import React, { useEffect, useState, useContext, useRef, useMemo, useCallback } from "react";
import { AppContext } from "../../context/AppContext";
import useHttp from "../../hooks/useHttp";
import VoiceSelect from "./common/VoiceSelect";
import { EmptyListView, RenderNameWithEllipsis, UpgradeAlertPopup } from "../../pages/utils/CommonVessels";
import { LoadingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useVoiceContext } from "../context/VoiceContext";
import { useVoiceNumbers } from "../hooks/useVoiceNumbers";
import { ConnectingDot, getCurrentFeature, transcriptionStatus } from "../utils/VoiceUtils";
import CallHistoryDetails from "./call-screens/CallHistoryDetails";
import HTableWithTabs from "../../components/custom/table/HTableWithTabs";
import "../assets/css/base-styles.css";

dayjs.extend(utc);

const VoiceCallLog = ({
  mobileView,
  handleDialPadHistoryClickToCall,
  setCallDetails,
  setOpenCallDetails,
  openCallDetails,
  callDetails,
  fetchCallLogs,
}) => {
  const [appProperties] = useContext(AppContext);
  const listRef = useRef(null);
  const scrollPosition = useRef(0);
  const tableRef = useRef();

  useEffect(() => {
    if (!openCallDetails && listRef.current) {
      listRef.current.scrollTop = scrollPosition.current;
    }
  }, [openCallDetails]);

  const handleScroll = () => {
    if (listRef.current) {
      scrollPosition.current = listRef.current.scrollTop;
    }
  };
  const [upgradeAlertPopup, setUpgradeAlertPopup] = useState(false);
  const [callProperties, setCallProperties] = useVoiceContext();
  const { fetchData } = useHttp();
  const [selectedMenuItem, setSelectedMenuItem] = useState("all");
  // Note: callLogs state kept for mobile view compatibility (mobile view uses List component, not HTable)
  const [callLogs, setCallLogs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentRecordingUrls, setCurrentRecordingUrls] = useState([]);
  const [transcriptModalLoading, setTranscriptModalLoading] = useState(false);
  const [phoneNumberList, setPhoneNumberList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState("all");
  const [modalData, setModalData] = useState({});
  const [voiceIntegId, setVoiceIntegId] = useState();
  const { getVoiceSavedNumbers, getTranscriptionUtterances } = useVoiceNumbers();
  const [transcriptionModalData, setTranscriptionModalData] = useState({});
  const [isTranscriptionModalOpen, setIsTranscriptionModalOpen] = useState(false);
  const [callSummarySetting, setCallSummarySetting] = useState(false);
  const audioRef = useRef(null);

  const getSavedNumber = async () => {
    if (voiceIntegId) {
      setLoading(true);
      getVoiceSavedNumbers("all")
        .then((response) => {
          const phoneNumbersArray = Array.isArray(response)
            ? response
            : response?.numberVoiceConfigurationEntities || [];
          const phoneNumbers = phoneNumbersArray?.map((item) => item.phoneNumber) || [];
          setPhoneNumberList(phoneNumbers);
        })
        .catch((error) => console.error("Error fetching or parsing data:", error))
        .finally(() => setLoading(false));
    }
  };

  const formatCallData = (call) => {
    const durationInSeconds = call?.callDuration || 0;
    const hours = Math.floor(durationInSeconds / 3600);
    const minutes = Math.floor((durationInSeconds % 3600) / 60);
    const seconds = durationInSeconds % 60;

    let formattedDuration = "";
    if (seconds > 0) formattedDuration = `${seconds}s`;
    if (minutes > 0) formattedDuration = `${minutes}m ${formattedDuration}`;
    if (hours > 0) formattedDuration = `${hours}h ${formattedDuration}`;

    const formattedUtcDuration = dayjs.utc(durationInSeconds * 1000).format("HH:mm:ss");

    const formattedDate = dayjs(call?.createdTime).format("D MMM YYYY h:mm A");
    const recordingUrl = Array.isArray(call?.recordingUrls) ? call?.recordingUrls.find((url) => url !== null) : null;
    const isMissed = call?.callStatus === "missed" || call?.callStatus === "ringing";
    const isCompleted = call?.callStatus === "completed";
    const voiceMailRecordingUrl = call?.voiceMailRecordingUrl;
    const customerNumber = call?.callDirection === "INBOUND" ? call?.fromNumber : call?.toNumber;
    const phoneNumber = call?.callDirection === "INBOUND" ? call?.toNumber : call?.fromNumber;
    const callNotes = call?.callDescription ? call?.callDescription.trim() : null;
    const contactName = call?.contactName ? call?.contactName.trim() : "";

    return {
      formattedDate,
      formattedDuration,
      formattedUtcDuration,
      recordingUrl,
      isMissed,
      isCompleted,
      voiceMailRecordingUrl,
      customerNumber,
      phoneNumber,
      callNotes,
      durationInSeconds,
      contactName,
    };
  };

  const openAudioPlayerModal = (urls, title, customerNumber, formattedDate, iconBasedOnDirection) => {
    setCurrentRecordingUrls(Array.isArray(urls) ? urls : [urls]);
    setModalData({ 
      title: title, 
      customerNumber: customerNumber, 
      formattedDate: formattedDate, 
      iconBasedOnDirection: iconBasedOnDirection,
      isVoicemail: title === "Voicemail"
    });
    setIsModalOpen(true);
  };
  const antIcon = <LoadingOutlined className="loading-button" spin />;

  const customIcon = <Spin indicator={antIcon} />;

  const onAudioPlayerModalClose = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsModalOpen(false);
    setCurrentRecordingUrls([]);
  };

  const handleClickToCall = (customerNumber, phoneNumber, recordId, module, contactName) => {
    setCallProperties((prev) => ({
      ...prev,
      isDialPadVisible: true,
      callerNumber: customerNumber,
      callType: "outbound",
      callRecordId: recordId,
      callRecordModule: module,
      contactName: contactName !== "null" && contactName ? contactName : "",
    }));
  };
  useEffect(() => {
    if (appProperties?.installedApps) {
      const voiceIntegrations = appProperties?.installedApps?.filter((integration) => integration.right.serviceType === "VOICE");
      if (voiceIntegrations?.[0]?.integProps?.integId) {
        setVoiceIntegId(voiceIntegrations?.[0]?.integProps?.integId);
      }
    }
  }, [appProperties?.installedApps]);

  // Fetch phone number list on mount
  useEffect(() => {
    if (!phoneNumberList) {
      getSavedNumber();
    }
  }, []);

  const callLogTabs = [
    { key: "all", label: "All" },
    { key: "inbound", label: "Inbound" },
    { key: "outbound", label: "Outbound" },
    { key: "missedCalls", label: "Missed Calls" },
    { key: "voicemail", label: "Voice Mail" },
    { key: "blockedNumbers", label: "Blocked" },
    { key: "general", label: "General" },
  ];

  // Dynamic columns function based on active tab
  const getCallLogColumns = useMemo(() => {
    const blockedNumbersColumns = [
      {
        title: (
          <>
            <div className="d-flex flex-column justify-content-start ps-5"> Customer</div>
          </>
        ),
        dataIndex: "contacts",
        key: "contacts",
        ellipsis: true,
        width: 350,
      },
      { title: "User", dataIndex: "toNumber", key: "toNumber", ellipsis: true },
      { title: "Date & Time", dataIndex: "dateAndTime", key: "dateAndTime" },
    ];

    const callHeaderColumns = [
      {
        title: (
          <>
            <div className="d-flex flex-column justify-content-start ps-5"> Customer</div>
          </>
        ),
        dataIndex: "contacts",
        key: "contacts",
        ellipsis: true,
        width: 320,
      },
      { title: "User", dataIndex: "phoneNumber", key: "phoneNumber", ellipsis: true },
      { title: "Date & Time", dataIndex: "dateAndTime", key: "dateAndTime" },
      { title: "Duration", dataIndex: "duration", key: "duration" },
      { title: "Notes", dataIndex: "notes", key: "notes" },
      { title: "Recordings", dataIndex: "recordings", key: "recordings" },
      { title: "Voice Mail", dataIndex: "voiceMail", key: "voiceMail" },
      callSummarySetting ? { title: "Transcription", dataIndex: "transcription", key: "transcription" } : null,
    ].filter(Boolean);

    return selectedMenuItem === "blockedNumbers" ? blockedNumbersColumns : callHeaderColumns;
  }, [selectedMenuItem, callSummarySetting]);

  const phoneNumberOptions = useMemo(() => {
    return [
      { label: "All", value: "all" },
      ...(phoneNumberList?.map((number) => ({
        label: number,
        value: number,
      })) || []),
    ];
  }, [phoneNumberList]);

  const onPhoneNumberChange = (value) => {
    setSelectedPhoneNumber(value);
    // Reload table when phone number filter changes
    if (tableRef.current) {
      tableRef.current.reload?.();
    }
  };

  const callIconHandler = (callDirection, callStatus) => {
    if (callDirection === "INBOUND" && callStatus === "missed") {
      return "call-action-icons missedCallIcon";
    } else if (callDirection === "INBOUND" && callStatus === "blocked") {
      return "call-inbound-blocked call-action-icons";
    } else if (callDirection === "INBOUND") {
      return "callDirection-inbound call-action-icons";
    } else if (callDirection === "OUTBOUND" && callStatus === "blocked") {
      return "call-outbound-blocked call-action-icons";
    } else if (callDirection === "OUTBOUND") {
      return "callDirection-outbound call-action-icons";
    }
  };

  const callStatusHandler = (callDirection, callStatus, participantUserName) => {
    if (callDirection === "INBOUND" && callStatus === "ringing") {
      return "Missed";
    } else if (callDirection === "INBOUND" && callStatus === "missed") {
      return "Missed";
    } else if (callDirection === "INBOUND" && (callStatus === "completed" || callStatus === "answered")) {
      return "Answered";
    } else if (callDirection === "OUTBOUND" && (callStatus === "missed" || callStatus === "ringing")) {
      return "Unanswered";
    } else if (callDirection === "OUTBOUND" && callStatus === "completed") {
      return "Answered";
    }
  };

  const openTranscriptionModal = async (callDetails) => {
    try {
      setIsTranscriptionModalOpen(true);
      setTranscriptModalLoading(true);

      const { formattedDate, contactName, formattedUtcDuration } = formatCallData(callDetails);

      const resolvedContactName = contactName || (callDetails?.callDirection === "INBOUND" ? callDetails?.fromNumber : callDetails?.toNumber);

      const updateModalData = (data) => {
        setTranscriptionModalData({
          transcriptSummary: data?.transcriptSummary,
          transcriptStatus: data?.transcriptStatus,
          transcriptErrorMessage: data?.transcriptErrorMessage,
          transcriptUtterances: data?.transcriptUtterances,
          formattedDate,
          contactName: resolvedContactName,
          participantUserName: callDetails?.participantUserName ? callDetails?.participantUserName : callDetails?.callDirection === "INBOUND" ? callDetails?.toNumber : callDetails?.fromNumber,
          duration: formattedUtcDuration,
        });
      };

      if (callDetails?.transcriptionStatus === transcriptionStatus.UTTERANCES_GENERATED) {
        updateModalData(callDetails);
        setTranscriptModalLoading(false);
        return;
      }

      try {
        const response = await getTranscriptionUtterances(callDetails);
        console.log("response>>>>>>>>>>", response);

        tableRef?.current?.reload?.();
        updateModalData(response);
      } catch (apiError) {
        console.error("Error fetching transcription utterances:", apiError);
      } finally {
        setTranscriptModalLoading(false);
      }
    } catch (error) {
      console.error("Error opening transcription modal:", error);
      setTranscriptModalLoading(false);
    }
  };
  
  const handleCallHistoryInfoClick = (item) => {
    appProperties?.controller?.openRecord(callProperties?.loadedSDK, item?.associatedObjectId, item?.associatedObjectType, item?.recordLink);
  };

  const handleTranscriptionModalClose = () => {
    setIsTranscriptionModalOpen(false);
    setTranscriptionModalData({});
  };
  const formatCallLog = useCallback(
    (call, index, isBlockedTab = false) => {
      const {
        formattedDate,
        formattedUtcDuration,
        formattedDuration,
        recordingUrl,
        isMissed,
        isCompleted,
        voiceMailRecordingUrl,
        customerNumber,
        phoneNumber,
        contactName,
      } = formatCallData(call);
      const iconBasedOnDirection = callIconHandler(call?.callDirection, call?.callStatus);

      if (isBlockedTab) {
        return {
          key: index + 1,
          contacts: (
            <div className="d-flex flex-row align-items-center  contact-item">
              <div className="col-1">
                <div className={`${iconBasedOnDirection}`} />
              </div>
              <div className="d-flex flex-column justify-content-start ms-3">
                {contactName && contactName !== "null" && (
                  <div>
                    <RenderNameWithEllipsis content={contactName} />
                  </div>
                )}
                <div>{customerNumber}</div>
              </div>
            </div>
          ),
          toNumber: call?.callDirection === "INBOUND" ? call?.toNumber : call?.fromNumber,
          dateAndTime: formattedDate,
          callStatus: call?.callStatus,
        };
      }

      return {
        key: index + 1,
        contacts: (
          <div className="d-flex flex-row align-items-center  contact-item">
            <div className="col-1">
              <div className={`${iconBasedOnDirection}`} />
            </div>
            <div className="d-flex flex-column justify-content-start ms-3">
              {contactName && contactName !== "null" && (
                <div>
                  <RenderNameWithEllipsis content={contactName} />
                </div>
              )}
              <div className="d-flex flex-row justify-content-start align-items-center">
                <div>{customerNumber}</div>
                <div className="call-icon ms-0">
                  <div
                    className="call-action-icons logs-clickToCall-Icon"
                    onClick={() =>
                      handleClickToCall(customerNumber, phoneNumber, call?.associatedObjectId, call?.associatedObjectType, call?.contactName)
                    }
                  />
                </div>
              </div>
              <div
                className={`ms-1 ${
                  callStatusHandler(call?.callDirection, call?.callStatus, call?.participantUserName) === "Missed" && "hs-color-red"
                }`}
              >
                {call?.errorMessage ? (
                  <div className="d-flex flex-row align-items-center">
                    <div className="hs-color-red">Error</div>
                    <Tooltip placement="rightTop" title={call?.errorMessage}>
                      <div className="actionIconsSprite call-log-error-icon"></div>
                    </Tooltip>
                  </div>
                ) : (
                  callStatusHandler(call?.callDirection, call?.callStatus, call?.participantUserName)
                )}
              </div>
            </div>
          </div>
        ),
        phoneNumber: (
          <div className="">
            {call?.participantUserName ? <RenderNameWithEllipsis content={call?.participantUserName ? call?.participantUserName : "-"} /> : null}
            <div>{phoneNumber}</div>
          </div>
        ),
        dateAndTime: formattedDate,
        duration: call.callDuration ? formattedUtcDuration : "00:00:00",
        notes:
          call?.callDescription && call?.callDescription.trim() ? (
            <Tooltip
              placement="rightTop"
              title={<div style={{ maxHeight: "150px", overflowY: "auto", whiteSpace: "pre-wrap" }}>{call?.callDescription}</div>}
            >
              <div className="callDescriptionIcon call-action-icons cursor-pointer" />
            </Tooltip>
          ) : (
            <div className="ms-2">-</div>
          ),
        recordings: recordingUrl ? (
          <div
            className="call-action-icons recordingsPlayIcon cursor-pointer ms-lg-3 "
            onClick={() => openAudioPlayerModal(call?.recordingUrls, "Call Recording", customerNumber, formattedDate, iconBasedOnDirection)}
          ></div>
        ) : (
          <div className="ms-lg-4"> -</div>
        ),
        voiceMail: voiceMailRecordingUrl ? (
          <div
            className="call-action-icons recordingsPlayIcon cursor-pointer ms-lg-3"
            onClick={() => openAudioPlayerModal(voiceMailRecordingUrl, "Voicemail", customerNumber, formattedDate, iconBasedOnDirection)}
          ></div>
        ) : (
          <div className="ms-lg-4"> -</div>
        ),
        transcription: call?.transcriptionStatus >= transcriptionStatus.TRANSCRIBED ? (
          <div className="ms-lg-4">
            <div className="call-action-icons transcription-icon cursor-pointer" onClick={() => openTranscriptionModal(call)}></div>
          </div>
        ) : (
          <div className="ms-lg-4"> -</div>
        ),
      };
    },
    [handleClickToCall, callStatusHandler, callIconHandler, openAudioPlayerModal]
  );
  
  const handleOpenCallDetails = (item) => {
    setOpenCallDetails(true);
    setCallDetails(item);
  };

  const transcriptionTableHeaderColumns = [
    {
      title: "Time",
      dataIndex: "time",
      key: "time",
      width: 20,
    },
    {
      title: "Speaker",
      dataIndex: "speaker",
      key: "speaker",
      width: 20,
    },
    {
      title: "Transcript",
      dataIndex: "transcript",
      key: "transcript",
      width: 100,
    },
  ];
  useEffect(() => {
    if (voiceIntegId) {
      tableRef?.current?.reload?.();
    }
  }, [voiceIntegId]);

  // Fetch call logs for mobile view
  const fetchMobileCallLogs = useCallback(async () => {
    if (!voiceIntegId || !mobileView) {
      return;
    }
    
    try {
      setLoading(true);
      let url = `voice/logs?callFilterType=ALL&integId=${voiceIntegId}&limit=100`;
      const response = await fetchData(url, "GET", null, appProperties);
      
      if (!response) {
        setCallLogs([]);
        return;
      }
      const responseData = typeof response === "string" ? JSON.parse(response) : response;
      const callLogsResponses = responseData.data?.logs || responseData.data?.data?.logs || [];
      const rawCallLogs = callLogsResponses.map((item) => {
        const voiceLogsEntity = item.voiceLogsEntity || item;        
        return voiceLogsEntity;
      }).filter(Boolean);

      setCallLogs(rawCallLogs);
    } catch (error) {
      console.error("Error fetching call logs for mobile view:", error);
      setCallLogs([]);
    } finally {
      setLoading(false);
    }
  }, [voiceIntegId, mobileView, selectedMenuItem, selectedPhoneNumber, fetchData, appProperties]);

  // Fetch call logs when voiceIntegId is available or when fetchCallLogs prop changes
  useEffect(() => {
    if (mobileView && voiceIntegId) {
      fetchMobileCallLogs();
    }
  }, [mobileView, voiceIntegId, fetchCallLogs, selectedMenuItem, selectedPhoneNumber, fetchMobileCallLogs]);

  // Request function for HTable with server-side pagination
  const fetchCallLogsData = useCallback(
    async ({ cursor, limit, signal, selectedMenuItem: menuItem, selectedPhoneNumber: phoneNumber, voiceIntegId: integId, searchKeyword }) => {
      if(!integId) {
        return { data: [], success: false, nextCursor: null };
      };
      try {
        // Map menu item to callFilterType
        let callFilterType = "ALL";
        if (menuItem === "inbound") {
          callFilterType = "INBOUND";
        } else if (menuItem === "outbound") {
          callFilterType = "OUTBOUND";
        } else if (menuItem === "missedCalls") {
          callFilterType = "MISSED";
        } else if (menuItem === "voicemail") {
          callFilterType = "VOICEMAIL";
        } else if (menuItem === "blockedNumbers") {
          callFilterType = "BLOCKED";
        } else if (menuItem === "general") {
          callFilterType = "ALL"; // General filter is handled client-side for now
        }

        // Build URL with query parameters
        let url = `voice/logs?callFilterType=${callFilterType}&integId=${integId}&limit=${limit}`;
        
        // Add cursor if present
        if (cursor?.cursor) {
          url += `&cursor=${encodeURIComponent(cursor.cursor)}`;
        } else if (cursor?.lastKey) {
          url += `&lastKey=${encodeURIComponent(cursor.lastKey)}`;
        }

        if (searchKeyword) {
          url += `&search=${encodeURIComponent(searchKeyword)}`;
        }

        // Make API call
        const response = await fetchData(url, "GET", null, appProperties, null, null, signal);
        
        if (!response) {
          return { data: [], success: false, nextCursor: null };
        }

        // Parse response
        const responseData = typeof response === "string" ? JSON.parse(response) : response;
        console.log("responseData", responseData);
        const callLogsResponses = responseData.data?.logs || responseData.data?.data?.logs || [];
        const callSummarySetting = responseData.data?.callSummarySetting || responseData.data?.data?.callSummarySetting;
        if(callSummarySetting === "true") {
          setCallSummarySetting(true);
        } else {
          setCallSummarySetting(false);
        }
        const paginationModel = responseData.data?.paginationModel || responseData.paginationModel || {};


        // Extract voiceLogsEntity from each CallLogsResponse and format
        const isBlockedTab = menuItem === "blockedNumbers";
        const formattedLogs = callLogsResponses.map((item, index) => {
          const voiceLogsEntity = item.voiceLogsEntity || item;
          // Apply phone number filter client-side if needed (backend doesn't support it yet)
          if (phoneNumber !== "all") {
            const matchesPhone = voiceLogsEntity.toNumber === phoneNumber || voiceLogsEntity.fromNumber === phoneNumber;
            if (!matchesPhone) {
              return null;
            }
          }
          // Apply general filter client-side (backend doesn't support it yet)
          if (menuItem === "general" && voiceLogsEntity.notifiedTeamId) {
            return null;
          }
          return formatCallLog(voiceLogsEntity, index, isBlockedTab);
        }).filter(Boolean);

        // Build nextCursor from paginationModel
        const nextCursor = paginationModel.cursor
          ? { cursor: paginationModel.cursor }
          : paginationModel.lastKey
          ? { lastKey: paginationModel.lastKey }
          : null;

        return {
          data: formattedLogs,
          success: true,
          nextCursor,
        };
      } catch (error) {
        console.error("Error fetching call logs:", error);
        return { data: [], success: false, nextCursor: null };
      }
    },
    [formatCallLog, fetchData, appProperties]
  );

  function formatTime(createdTime) {
    return dayjs(createdTime).format("hh:mm A");
  }
  const formatDate = (date) => {
    return dayjs(date).format("DD MMM");
  };
  const formatTimeDisplay = (createdTime) => {
    const now = new Date();
    const createdDate = new Date(createdTime);

    const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const createdDateOnly = new Date(createdDate.getFullYear(), createdDate.getMonth(), createdDate.getDate());

    const differenceInDays = Math.floor((nowDateOnly - createdDateOnly) / (1000 * 60 * 60 * 24));

    if (differenceInDays === 0) {
      return formatTime(createdDate);
    } else if (differenceInDays === 1) {
      return "Yesterday";
    } else {
      return formatDate(createdDate);
    }
  };

  function formatUtteranceDuration(ms) {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
  
    // Format as hh:mm:ss (or mm:ss if less than 1 hour)
    if (hours > 0) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    } else {
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
  }
  
  function getUtteranceDuration(start, end) {
    const durationMs = end - start;
    return formatUtteranceDuration(durationMs);
  }
  

  
  return (
    <div className={`h-100 ${mobileView ? "" : "px-3 py-2"}`}>
      {!mobileView ? (
        <div className="d-flex flex-column w-100 h-100 overflow-hidden p-lg-2 ">
          <HTableWithTabs
            key={`${selectedMenuItem}-${selectedPhoneNumber}`}
            tableRef={tableRef}
            tabBarExtraContent={
              phoneNumberList?.length > 1 ? (
                <div className="ps-3 d-flex justify-content-end align-items-center">
                  <VoiceSelect
                    optionSpecification={false}
                    options={phoneNumberOptions}
                    customClass="hs-h-40-px mt-2"
                    value={selectedPhoneNumber}
                    onChange={(value) => onPhoneNumberChange(value)}
                  />
                </div>
              ) : null
            }
            ui={{
              search: true,
              columns: getCallLogColumns,
              rowKey: "key",
              hidePagination: false,
              heightToBeSubtractedFromContainerHeight: 60,
            }}
            data={{
              request: fetchCallLogsData,
              requestExtraParams: {
                selectedMenuItem,
                selectedPhoneNumber,
                voiceIntegId,
              },
            }}
            tabMenuItems={callLogTabs}
            defaultActiveKey={selectedMenuItem}
            onChange={(key) => {
              setSelectedMenuItem(key);
              // Reload table when tab changes
              if (tableRef.current) {
                tableRef.current.reload?.();
              }
            }}
            proProps={{
              locale: {
                emptyText: (
                  <div style={{ height: "55dvh" }}>
                    <div className="d-flex flex-grow-* gap-5 flex-column align-items-center justify-content-center h-100">
                      <div className="d-flex gap-3 flex-column align-items-center">
                        <div className="callCardIcons emptyVoiceCallListView display-inline-block"></div>
                        <div className="d-flex flex-column align-items-center justify-content-center">
                          <div className="hs-fs-16">No Call Record</div>
                          <div className="hs-fs-12">Your Recent Record will appear here</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              },
            }}
          />
          <Modal
            open={isTranscriptionModalOpen}
            onCancel={handleTranscriptionModalClose}
            loading={transcriptModalLoading}
            title={
              <div className = "border-bottom d-flex gap-5 align-items-center">
              <div className="d-flex align-items-center gap-2 py-2">
                <div className="call-action-icons transcription-modal-icon cursor-pointer" />
                <div>
                  <div>Call Transcription</div>
                  <div className="hs-fs-11 hs-color-light-grey-2">{transcriptionModalData?.formattedDate}</div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2 hs-bg-light-violet px-2 py-2 rounded-4 hs-border-violet">
                <div className="actionIconsSprite smallSize-announcement"></div>
            <div className="hs-fs-13 hs-fw-400 hs-color-violet">AI Generated</div> 
            </div>
            </div>
            }
            footer={null}
            width={900}
          >
            <div className="d-flex flex-column gap-3 pt-2">
              <div className="d-flex gap-2 hs-bg-light-gray p-3 hs-border-0-25-rem">
                <div className="d-flex align-items-center">
                  <div className="call-action-icons call-transcription-duration-icon" />
                  <div>Duration: {transcriptionModalData?.duration}</div>
                </div>
                <Divider type="vertical" style={{ height: "20px", color: "var(--hs-dark-grey)" }} />
                <div className="d-flex align-items-center">
                  <div className="call-action-icons call-transcription-participants-icon" />
                  <div>
                    Participants: {transcriptionModalData?.participantUserName? transcriptionModalData?.participantUserName : "-"}, {transcriptionModalData?.contactName? transcriptionModalData?.contactName : "-"}
                  </div>
                </div>
              </div>
              {transcriptionModalData?.transcriptSummary && (
                <div>
                  <div className="hs-fs-14 hs-fw-500">Summary: {transcriptionModalData?.transcriptSummary}</div>
                </div>
              )}
              <div>
                  <Table
                    scroll={{ y: 340 }}
                    columns={transcriptionTableHeaderColumns}
                    bordered={true}
                    dataSource={transcriptionModalData?.transcriptUtterances?.map((item) => {
                      return {
                        time: getUtteranceDuration(item?.start, item?.end),
                        speaker: item?.speaker,
                        transcript: item?.text,
                      };
                    })}
                    pagination={false}
                  />
              </div>
            </div>
          </Modal>
          <Modal
            className="audio-player hs-bg-white"
            open={isModalOpen}
            style={{ borderRadius: 20 }}
            onCancel={onAudioPlayerModalClose}
            footer={null}
            title={modalData?.title}
          >
            <div className="d-flex justify-content-center gap-5 flex-column w-100 p-2 pt-4">
              <div>
                <div className="d-flex align-items-center gap-2">
                  <div className={`${modalData?.iconBasedOnDirection}`} />
                  <div className="gap-1 d-flex flex-column">
                    <div className="hs-fs-14 hs-fw-500">{modalData?.customerNumber}</div>
                    <div className="hs-fs-11 hs-color-light-grey-2">{modalData?.formattedDate}</div>
                  </div>
                </div>
              </div>
              <div className="d-flex flex-column gap-3">
                {currentRecordingUrls?.map((url, index) => (
                  <div key={index} className="d-flex flex-column gap-2">
                    <audio ref={audioRef} src={url} controls className="w-100" />
                  </div>
                ))}
              </div>
            </div>
          </Modal>
          {upgradeAlertPopup && <UpgradeAlertPopup upgradeAlertPopup={upgradeAlertPopup} setUpgradeAlertPopup={setUpgradeAlertPopup} />}
        </div>
      ) : !openCallDetails ? (
        <div>
          <div
            ref={listRef}
            className="p-3"
            style={{
              maxHeight: "620px",
              overflowY: "auto",
            }}
            onScroll={handleScroll}
          >
            <List
              rootClassName="callHistoryList"
              itemLayout="horizontal"
              loading={{ spinning: loading, indicator: customIcon }}
              dataSource={callLogs}
              renderItem={(item, index) => {
                const { customerNumber, phoneNumber, formattedDuration, recordingUrl, callNotes, contactName } = formatCallData(item);
                if (item?.callStatus === "blocked") {
                  return null;
                }
                return (
                  <List.Item className="text-white call-history-item cursor-pointer" onClick={() => handleOpenCallDetails(item)}>
                    <List.Item.Meta
                      className="text-white d-flex align-items-center"
                      avatar={<div className={`${callIconHandler(item?.callDirection, item?.callStatus)}`}></div>}
                      title={
                        <div className="call-title-container">
                          <div className="d-flex flex-column">
                            <div className="hs-fs-16">
                              <span className="text-white hs-fs-13 hs-fw-500">
                                {item?.contactName && item?.contactName !== "null" ? item?.contactName : ""}
                              </span>
                            </div>
                            <div className="d-flex flex-row">
                              <span className="text-white hs-fs-13 hs-fw-500">
                                {item?.callDirection === "INBOUND" ? item?.fromNumber : item?.toNumber}
                              </span>
                              <span className="call-icon">
                                <div
                                  className="call-action-icons logs-clickToCall-Icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDialPadHistoryClickToCall(
                                      item?.contactName && item?.contactName !== "null" ? item?.contactName : "",
                                      customerNumber,
                                      phoneNumber,
                                      item?.associatedObjectId,
                                      item?.associatedObjectType
                                    );
                                  }}
                                />
                              </span>
                            </div>
                          </div>
                        </div>
                      }
                      description={
                        <div
                          className={` hs-fw-400 hs-fs-12 ${
                            callStatusHandler(item?.callDirection, item?.callStatus, item?.participantUserName) === "Missed"
                              ? "hs-color-red"
                              : "hs-color-md-grey"
                          }`}
                        >
                          {item?.errorMessage ? (
                            <div className="d-flex flex-row align-items-center">
                              <div className="hs-color-red">Error</div>
                              <Tooltip placement="rightTop" title={item?.errorMessage}>
                                <div className="actionIconsSprite call-log-error-icon"></div>
                              </Tooltip>
                            </div>
                          ) : (
                            callStatusHandler(item?.callDirection, item?.callStatus, item?.participantUserName)
                          )}
                          <div className="d-flex flex-row">
                            <div className="hs-fs-11 hs-color-md-grey">{formatTimeDisplay(item?.createdTime)}</div>
                            {formattedDuration && (
                              <div className="d-flex flex-row">
                                <div className="call-action-icons dot-icon" />
                                <div>{formattedDuration}</div>
                              </div>
                            )}
                            {recordingUrl && <div className="call-action-icons call-history-callRecord-icon " />}
                            {callNotes && <div className="call-action-icons call-history-callNote-icon  " />}
                          </div>
                        </div>
                      }
                    />
                    {item?.associatedObjectId &&
                      item?.associatedObjectType &&
                      item?.associatedObjectType !== "undefined" &&
                      item?.associatedObjectId !== "undefined" && (
                        <div
                          className="call-details-icon call-action-icons cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCallHistoryInfoClick(item);
                          }}
                        />
                      )}
                  </List.Item>
                );
              }}
              locale={{
                emptyText: (
                  <EmptyListView
                    tagLineOne={<div className="text-white">No call history available</div>}
                    icon={`call-action-icons empty-call-history-icon  display-inline-block`}
                    tagLineTwo={`Your recent calls will appear here`}
                    tagHeight={"35vh"}
                  />
                ),
              }}
            />
          </div>
        </div>
      ) : (
        <CallHistoryDetails callDetails={callDetails} setOpenCallDetails={setOpenCallDetails} />
      )}
    </div>
  );
};

export default VoiceCallLog;
